/**
 * Toast helpers — the standard feedback after every save/send/delete (UX-097).
 *
 * Pattern:
 *   - Every create / save / send / delete ends with ONE toast that names the
 *     thing: "Jane Smith added", "Quote sent to Brad Holloway",
 *     "Business hours saved". Not "Success", not "Saved successfully!".
 *   - Creates offer the new record: toast.success('Jane Smith added', { action: { label: 'Open', onClick } })
 *     (or open/highlight it directly and skip the action).
 *   - Failures: toast.error(err, 'send the quote') →
 *     title "Couldn't send the quote", message from describeError() (never a
 *     raw status code; offline/forbidden get their own wording).
 *   - Validation problems are inline on the field, never a toast (UX-102).
 *
 * Usable outside React (mutation callbacks, stores).
 */
import { useUIStore } from '@/stores/uiStore'
import { describeError } from '@/lib/errors'

type ToastAction = { label: string; onClick: () => void }

function push(type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string, opts: { action?: ToastAction; duration?: number } = {}) {
  useUIStore.getState().addToast({ type, title, message, action: opts.action, duration: opts.duration })
}

export const toast = {
  /** "Quote sent to Brad Holloway" · optional one-line detail · optional action. */
  success(title: string, opts: { message?: string; action?: ToastAction; duration?: number } = {}) {
    push('success', title, opts.message, opts)
  },
  /** "Business hours saved". */
  saved(thing: string, opts: { action?: ToastAction } = {}) {
    push('success', `${thing.charAt(0).toUpperCase()}${thing.slice(1)} saved`, undefined, opts)
  },
  info(title: string, opts: { message?: string; action?: ToastAction; duration?: number } = {}) {
    push('info', title, opts.message, opts)
  },
  warning(title: string, opts: { message?: string; action?: ToastAction; duration?: number } = {}) {
    push('warning', title, opts.message, opts)
  },
  /**
   * toast.error(err, 'save business hours')
   *   → "Couldn't save business hours" · "Something went wrong on our side. Try again in a minute."
   * Pass a string instead of an error for a custom message.
   */
  error(errOrMessage: unknown, action?: string, opts: { forbiddenWhat?: string; actionButton?: ToastAction } = {}) {
    if (typeof errOrMessage === 'string' && !action) {
      push('error', errOrMessage, undefined, { action: opts.actionButton })
      return
    }
    const d = describeError(errOrMessage, { action, forbiddenWhat: opts.forbiddenWhat })
    const title = d.kind === 'forbidden' || d.kind === 'offline' || d.kind === 'unauthorized' ? d.title : `Couldn't ${action ?? 'finish that'}`
    push('error', title, d.message, { action: opts.actionButton })
  },
}
